import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { EditorState } from 'draft-js';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery } from '@apollo/client';
import {
  Modal,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  FormFeedback,
  CustomInput,
  Row,
  Col,
  Alert
} from 'reactstrap';

import {
  GQL_PAYMENTLINK_CREATE_BY_SLUG,
  GQL_PAYMENTLINK_UPDATE_BY_SLUG,
  GQL_GET_USER_CONTENTS,
  GQL_INSERT_USER_CONTENT_FILE,
  GQL_DELETE_MULTIPLE_FILE,
  GQL_GET_VIDEO_EMBED_BY_PAYMENT_LINK_ID,
  GQL_UPSERT_VIDEO_EMBED,
  GQL_INSERT_VIDEO_EMBED_FILE,
  GQL_UPDATE_VIDEO_EMBED_FILE
} from '../../gqls';
import {
  validator,
  paymentLinkUrl,
  sanitizeDOM,
  REGEX_SLUG,
  paymentTypeOptions,
  slug,
  isYouTubeUrl
} from '../../utils';
import { updatePaymentLinks } from '../../actions/payment-links';
import { CustomStyles, CustomStylesInvalid } from '../custom-styles/react-select-styles';
import Icon from '../icon';
import DatePicker from '../date-time-picker';
import Dropzone from '../dropzone';
import LoadingAnimation from '../loading-animation';
import SubmitButton from '../submit-button';
import RichEditor, { getPlainText, getContentString, getEditorContent } from '../rich-text-editor';
import MultipleImageVideoInput from '../multiple-image-video-input';
import MayarAlert from '../alert/alert';
import VideoDrmUpload from '../video-drm-upload';

const ModalBodyDigitalProduct = ({ isOpen = false, onClose, onSuccess, data
}) => {
  if (!isOpen) return null;

  const multipleCoverContent = useSelector(state => state.multipleCoverContent);
  const currentUser = useSelector(state => state.currentUser);
  const [isolatedMultipleCoverContent, setIsolatedMultipleCoverContent] = useState([]); // state for modal create product only

  const stateEdit = EditorState.createEmpty();
  const [useRedirect, setUseRedirect] = useState(data?.redirectUrl ? true : false);
  const [sourceFile, setSourceFile] = useState(data && !data.redirectUrl ? 'existing' : null);
  const [multipleFileId, setMultipleFileId] = useState(
    !data?.redirectUrl ? data?.content?.map(item => item?.file?.id) : null
  );
  const [notAllowed, setNotAllowed] = useState(false);
  const [fileOptions, setfileOptions] = useState([]);
  const [paymentType, setPaymentType] = useState(data?.paymentType || 'paid');

  const [editorState, setEditorState] = useState(stateEdit);
  const [description, setDescription] = useState('');
  const [textDescription, setTextDescription] = useState('');
  const [check, setCheck] = useState(true);
  const [invalidDescription, setInvalidDescription] = useState(false);

  const [multipleImageId, setMultipleImageId] = useState('');
  const [deletedCoverContents, setDeletedCoverContents] = useState([]);

  const [disableSubmitButton, setDisableSubmitButton] = useState(false);
  const [enableAffiliate, setEnableAffiliate] = useState(Boolean(data?.affiliateCommission));
  const [editSuccess, setEditSuccess] = useState(false);

  const [videoEmbedUrlPreview, setVideoEmbedUrlPreview] = useState('');
  const [videoEmbedRowId, setVideoEmbedRowId] = useState(null);
  const [videoEmbedFileId, setVideoEmbedFileId] = useState(null);
  const [uploadedVideoEmbedUrl, setUploadedVideoEmbedUrl] = useState('');
  const [existingVideoEmbedUrl, setExistingVideoEmbedUrl] = useState('');
  const videoDrmUploadRef = useRef(null);

  const [insertPaymentLinkBySlug, { loading: insertLoading, data: newpl }] = useMutation(
    GQL_PAYMENTLINK_CREATE_BY_SLUG
  );
  const [editPaymentLinkBySlug, { loading: updateLoading }] = useMutation(GQL_PAYMENTLINK_UPDATE_BY_SLUG);
  const [userUserContent, { loading: insertUserContentLoading }] = useMutation(
    GQL_INSERT_USER_CONTENT_FILE
  );
  const [removeUserContent, { loading: removeUserContentLoading }] = useMutation(GQL_DELETE_MULTIPLE_FILE);
  const [upsertVideoEmbed, { loading: upsertVideoEmbedLoading }] = useMutation(GQL_UPSERT_VIDEO_EMBED);
  const [insertVideoEmbedFile, { loading: insertVideoEmbedFileLoading }] = useMutation(GQL_INSERT_VIDEO_EMBED_FILE);
  const [editVideoEmbedFile, { loading: editVideoEmbedFileLoading }] = useMutation(GQL_UPDATE_VIDEO_EMBED_FILE);

  const { loading: loadingFiles, data: dataFiles } = useQuery(GQL_GET_USER_CONTENTS, {
    variables: {
      limit: 100,
      offset: 0
    },
    context: { v2: true }
  });

  useQuery(GQL_GET_VIDEO_EMBED_BY_PAYMENT_LINK_ID, {
    variables: { paymentLinkId: data?.id },
    skip: !data,
    onCompleted: result => {
      const row = result?.getVideoEmbedByPaymentLinkId;
      if (row?.file?.filename) {
        setVideoEmbedRowId(row.id);
        setVideoEmbedFileId(row.file.id);

        // File.tag now just holds the merchant's accountId (same as every
        // other uploaded file), so DRM vs plain link is told apart purely
        // by URL shape - only our own VideoDrmUpload ever produces this
        // exact play-URL format.
        if (/video\.bunnycdn\.com\/play\//.test(row.file.filename)) {
          setSourceFile('video-drm');
          setUploadedVideoEmbedUrl(row.file.filename);
          setExistingVideoEmbedUrl(row.file.filename);
        } else {
          setVideoEmbedUrlPreview(row.file.filename);
          setSourceFile('link');
        }
      }
    }
  });

  const { register, handleSubmit, errors, control } = useForm();
  const [submitError, setSubmitError] = useState(null);
  const [date, setDate] = useState(data ? data.expiredAt : null);
  const [link, setLink] = useState(data ? data.link : '');
  const dispatch = useDispatch();
  const isUpdate = !data ? false : true;

  if (!data) data = {};

  const setEditor = content => {
    setEditorState(content);
    setDescription(getContentString(content));
    setTextDescription(getPlainText(content));
  };

  useEffect(() => {
    if (textDescription.length <= 0 && !check) {
      setInvalidDescription(true);
    } else {
      setInvalidDescription(false);
    }
  }, [textDescription]);

  useEffect(() => {
    if (isUpdate) {
      const content = getEditorContent(data.description);
      setEditor(content);
    }
  }, [data]);

  const onSubmit = async values => {
    if (textDescription.length <= 0) {
      setCheck(false);
      setInvalidDescription(true);
      return;
    }

    delete values.fileSource;
    delete values.useRedirect;
    let videoEmbedUrl =
      sourceFile === 'video-drm' ? uploadedVideoEmbedUrl || existingVideoEmbedUrl : values.videoEmbedUrl;
    // Set only when a video is freshly uploaded this submit - createBunnyVideo
    // already minted a File row (with bunnyVideoId/bunnyLibraryId correctly
    // set) for it, so that File must be edited rather than orphaned in favor
    // of a redundant insertVideoEmbedFile call below.
    let videoDrmFileId = null;
    // File.tag follows the same convention as every other uploaded file
    // (the merchant's accountId, see uploadUserContent) rather than a
    // video-embed-specific value - keeps these File rows on the same
    // footing as regular uploads instead of inventing a second meaning for
    // the column. DRM vs plain link is told apart by URL shape instead (see
    // the classifier above and the video-drm alert below).
    const fileTag = currentUser?.accountId;
    delete values.videoEmbedUrl;
    if (!values.limit) {
      delete values.limit;
    } else {
      values.limit = parseInt(values.limit);
    }
    if (values.amount) values.amount = parseFloat(values.amount);
    if (values.paymentType === 'free') {
      values.amount = 0;
    } else if (values.paymentType === 'voluntary' && !values.amount) {
      values.amount = 0;
    }

    if (values.crossoutPrice) values.crossoutPrice = parseFloat(values.crossoutPrice);
    else values.crossoutPrice = null;

    if (values.affiliateCommission) values.affiliateCommission = parseInt(values.affiliateCommission);
    else values.affiliateCommission = null;

    if (date !== null) setDate(date);
    try {
      if (sourceFile === 'video-drm' && videoDrmUploadRef.current?.hasPendingFile?.()) {
        const uploaded = await videoDrmUploadRef.current.startUpload();
        videoEmbedUrl = uploaded?.url;
        videoDrmFileId = uploaded?.fileId;
      }

      if (isUpdate) {
        values.id = data.id;

        if (!useRedirect) values.redirectUrl = '';
        const editPaymentLink = await editPaymentLinkBySlug({
          variables: {
            input: {
              ...values,
              status: 'active',
              expiredAt: date,
              description: sanitizeDOM(description),
              ...(multipleImageId && { multipleImageId }),
              ...(deletedCoverContents.length && {
                deletedCoverContents: deletedCoverContents.map(item => {
                  if ((item.file?.mimetype || '').includes('video') || item?.videoId || item?.value) {
                    return {
                      videoMultipleFileId: item?.id
                    };
                  }
                  return {
                    imageId: item.editImage?.id || item.id
                  };
                })
              }),
              ...(multipleCoverContent.length && {
                orderedMultipleCoverContent: multipleCoverContent.map(item => {
                  if ((item.file?.mimetype || item.file?.type || '').includes('video') || item?.videoId || item?.value) {
                    return {
                      position: Number(item.position),
                      videoId: item.videoId || item.file?.id || item.value,
                      editId: item?.id
                    };
                  }
                  return {
                    position: Number(item.position),
                    imageId: item.editImage?.id || item.id
                  };
                })
              })
            }
          }
        });

        const contentId = editPaymentLink?.data?.editPaymentLinkBySlug?.id;

        if (!['link', 'video-drm'].includes(sourceFile) && multipleFileId?.length && !useRedirect) {
          if (data?.content?.length) {
            try {
              await removeUserContent({
                variables: {
                  ids: data?.content?.map(i => i.id)
                }
              });
            } catch (error) {
              // Same known backend issue as the link/video-drm branch below:
              // deleteMultipleMultipleFile can fail for some existing content
              // rows. Don't let that block the rest of the update (payment
              // type/amount changes, new file selection) - worst case the old
              // file is left orphaned instead of the whole save failing.
              window.analytics.track('Remove Old Digital Product Content - ERROR', {
                paymentLinkId: contentId,
                contentIds: data?.content?.map(i => i.id),
                message: error.message
              });
            }
          }

          await userUserContent({
            variables: {
              input: multipleFileId.map(item => ({ key: contentId, value: item }))
            }
          });
        }

        if (['link', 'video-drm'].includes(sourceFile) && data?.content?.length && !useRedirect) {
          try {
            await removeUserContent({ variables: { ids: data?.content?.map(i => i.id) } });
          } catch (error) {
            // Known backend issue: deleteMultipleMultipleFile can fail for some
            // existing content rows. Don't let that block switching this
            // product over to video-drm/link - worst case the old file is
            // left orphaned instead of the whole update failing.
            window.analytics.track('Remove Old Digital Product Content - ERROR', {
              paymentLinkId: contentId,
              contentIds: data?.content?.map(i => i.id),
              message: error.message
            });
          }
        }

        if (!useRedirect) {
          if (['link', 'video-drm'].includes(sourceFile) && videoEmbedUrl) {
            let fileId = videoDrmFileId || videoEmbedFileId;

            if (fileId) {
              await editVideoEmbedFile({
                variables: { input: { id: fileId, filename: videoEmbedUrl, tag: fileTag } }
              });
            } else {
              const insertedFile = await insertVideoEmbedFile({
                variables: { input: { filename: videoEmbedUrl, mimetype: 'text/embed-url', tag: fileTag } }
              });
              fileId = insertedFile?.data?.insertFile?.id;
            }

            await upsertVideoEmbed({ variables: { paymentLinkId: contentId, fileId } });
          } else if (videoEmbedRowId) {
            try {
              await removeUserContent({ variables: { ids: [videoEmbedRowId] } });
            } catch (error) {
              // Same known backend issue as the two branches above:
              // deleteMultipleMultipleFile can fail for some existing rows.
              // Don't let that block switching this product away from
              // video-drm/link - worst case the old video-embed row is left
              // orphaned instead of the whole update failing.
              window.analytics.track('Remove Old Digital Product Content - ERROR', {
                paymentLinkId: contentId,
                contentIds: [videoEmbedRowId],
                message: error.message
              });
            }
          }
        }

        dispatch(updatePaymentLinks(Math.random()));
        setEditSuccess(true);
      } else {
        if (!multipleFileId?.length && !useRedirect && !(['link', 'video-drm'].includes(sourceFile) && videoEmbedUrl)) return;
        const paymentLink = await insertPaymentLinkBySlug({
          variables: {
            input: {
              ...values,
              expiredAt: date,
              link: slug(values.name),
              type: 'digital_product',
              status: 'active',
              description: sanitizeDOM(description),
              ...(multipleImageId && { multipleImageId }),
              ...(deletedCoverContents.length && {
                deletedCoverContents: deletedCoverContents.map(item => {
                  if ((item.file?.mimetype || '').includes('video') || item?.videoId || item?.value) {
                    return {
                      videoMultipleFileId: item?.id
                    };
                  }
                  return {
                    imageId: item.editImage?.id || item.id
                  };
                })
              }),
              ...(isolatedMultipleCoverContent.length && {
                orderedMultipleCoverContent: isolatedMultipleCoverContent.map(item => {
                  if ((item.file?.mimetype || item.file?.type || '').includes('video') || item?.videoId || item?.value) {
                    return {
                      position: Number(item.position),
                      videoId: item.videoId || item.file?.id || item.value,
                      editId: item?.id
                    };
                  }
                  return {
                    position: Number(item.position),
                    imageId: item.editImage?.id || item.id
                  };
                })
              })
            }
          }
        });
        if (paymentLink?.data?.insertPaymentLinkBySlug.errorMessage) {
          throw new Error(paymentLink?.data?.insertPaymentLinkBySlug.errorMessage || 'Terjadi kesalahan saat membuat produk');
        }
        if (paymentLink && !useRedirect) {
          const paymentLinkId = paymentLink.data.insertPaymentLinkBySlug.id;

          if (!['link', 'video-drm'].includes(sourceFile) && multipleFileId?.length) {
            await userUserContent({
              variables: {
                input: multipleFileId.map(item => ({
                  key: paymentLinkId,
                  value: item
                }))
              }
            });
          }

          if (['link', 'video-drm'].includes(sourceFile) && videoEmbedUrl) {
            let fileId = videoDrmFileId;

            if (fileId) {
              await editVideoEmbedFile({
                variables: { input: { id: fileId, filename: videoEmbedUrl, tag: fileTag } }
              });
            } else {
              const insertedFile = await insertVideoEmbedFile({
                variables: { input: { filename: videoEmbedUrl, mimetype: 'text/embed-url', tag: fileTag } }
              });
              fileId = insertedFile?.data?.insertFile?.id;
            }

            await upsertVideoEmbed({ variables: { paymentLinkId, fileId } });
          }
        }
        //segment tracking
        window.analytics.track('Create Digital Product', values);
      }
    } catch (error) {
      setSubmitError(error.message || "Error! Silahkan coba lagi atau hubungi CS");
      window.analytics.track('Create Digital Product - ERROR', values);
    }
  };

  useEffect(() => {
    if (newpl && newpl.insertPaymentLinkBySlug && !newpl.insertPaymentLinkBySlug.errorMessage) {
      onSuccess(newpl.insertPaymentLinkBySlug.id);
    }
  }, [newpl]);

  useEffect(() => {
    // Was previously gated on `upl && userContent`, but userContent only
    // ever gets set for the plain-file upload path (see onSubmit) - link
    // and video-drm edits skip that mutation entirely, so the modal never
    // closed after a successful DRM/link edit. editSuccess is set once the
    // full update (including the video-embed branch) actually finishes.
    if (editSuccess) onClose();
  }, [editSuccess]);

  if (dataFiles && dataFiles.getUserContents && dataFiles.getUserContents.length && !fileOptions.length) {
    const options = dataFiles.getUserContents.map(i => {
      return { value: i.id, label: i.filename };
    });
    setfileOptions(options);
  }

  return (
    <Modal isOpen={isOpen} fade size="lg">
      <div className="modal-header">
        <h5 className="modal-title h2">{isUpdate ? 'Edit' : 'Buat'} Produk Digital</h5>
        <Button className="close" color="" onClick={onClose}>
          <Icon name="x" />
        </Button>
      </div>
      <ModalBody>
        <p>
          <small>Penjualan produk digital semakin mudah dengan otomasi akses konten dan halaman produk keren</small>
        </p>
        <hr />
        {submitError && <MayarAlert isError message={submitError} />}

        <Form onSubmit={handleSubmit(onSubmit)} id="create-payment-link">
          <FormGroup>
            <Label for="name">Nama Produk*</Label>
            <Input
              type="text"
              name="name"
              id="name"
              invalid={errors.name}
              defaultValue={data.name}
              innerRef={register({
                required: validator.required,
                minLength: validator.minLength(5)
              })}
            />
            <FormFeedback>{errors.name?.message}</FormFeedback>
          </FormGroup>
          {isUpdate && (
            <FormGroup>
              <Label for="name">Slug*</Label>
              <Input
                type="text"
                name="link"
                id="link"
                invalid={errors.link}
                defaultValue={data.link}
                onChange={e => setLink(e.target.value)}
                innerRef={register({
                  required: validator.required,
                  pattern: validator.pattern(REGEX_SLUG)
                })}
              />
              <FormFeedback>{errors.link?.message}</FormFeedback>
              <small>{'contoh : ' + paymentLinkUrl(link)}</small>
            </FormGroup>
          )}
          <FormGroup>
            <Label for="paymentType">Tipe Pembayaran</Label>
            <Controller
              name="paymentType"
              control={control}
              defaultValue={paymentType}
              render={({ onChange, name, value: defaultPaymentType }) => (
                <Select
                  id={name}
                  name={name}
                  options={paymentTypeOptions}
                  defaultValue={paymentTypeOptions.find(obj => obj.value === defaultPaymentType)}
                  placeholder="Pilih Tipe Pembayaran"
                  styles={!!errors.paymentType ? CustomStylesInvalid : CustomStyles}
                  className={`${!!errors.paymentType ? 'is-invalid' : ''}`}
                  onChange={({ value }) => {
                    setPaymentType(value);
                    onChange(value);
                  }}
                />
              )}
            />
          </FormGroup>
          {paymentType === 'paid' && (
            <Row>
              <Col sm="6">
                <FormGroup>
                  <Label for="amount">Harga*</Label>
                  <InputGroup>
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>Rp</InputGroupText>
                    </InputGroupAddon>
                    <Input
                      type="number"
                      name="amount"
                      id="amount"
                      invalid={errors.amount}
                      defaultValue={data.amount}
                      innerRef={register({
                        required: validator.required,
                        min: validator.min(1000),
                        max: validator.max(100_000_000)
                      })}
                    />
                    <FormFeedback>{errors.amount?.message}</FormFeedback>
                  </InputGroup>
                  <small>Penagihan ini menggunakan mata uang IDR (Rupiah)</small>
                </FormGroup>
              </Col>
              <Col sm="6">
                <FormGroup>
                  <Label for="crossoutPrice">Harga Coret (opsional)</Label>
                  <InputGroup>
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>Rp</InputGroupText>
                    </InputGroupAddon>
                    <Input
                      type="number"
                      name="crossoutPrice"
                      id="crossoutPrice"
                      defaultValue={data?.crossoutPrice}
                      invalid={Boolean(errors.crossoutPrice)}
                      innerRef={register({
                        min: validator.min(1000),
                        max: validator.max(100000000)
                      })}
                    />
                    <FormFeedback>{errors.crossoutPrice?.message}</FormFeedback>
                  </InputGroup>
                  <small>Harga coret harus lebih besar dari harga utama.</small>
                </FormGroup>
              </Col>
            </Row>
          )}
          {paymentType === 'voluntary' && (
            <FormGroup>
              <Label for="amount">Minimal Harga</Label>
              <InputGroup>
                <InputGroupAddon addonType="prepend">
                  <InputGroupText>Rp</InputGroupText>
                </InputGroupAddon>
                <Input
                  type="number"
                  name="amount"
                  id="amount"
                  invalid={errors.amount}
                  placeholder="0+"
                  defaultValue={data.amount}
                  innerRef={register({
                    // required: validator.required,
                    min: validator.min(0),
                    max: validator.max(100000000)
                  })}
                />
                <FormFeedback>{errors.amount?.message}</FormFeedback>
              </InputGroup>
              <small>Penagihan ini menggunakan mata uang IDR (Rupiah)</small>
            </FormGroup>
          )}

          <FormGroup>
            <Label for="description">Deskripsi*</Label>
            <RichEditor onEditorStateChange={setEditor} editorState={editorState} invalidState={invalidDescription} />
            <FormFeedback className={`${invalidDescription ? 'd-block' : ''}`}>Wajib diisi</FormFeedback>
          </FormGroup>

          <MultipleImageVideoInput
            productName={data.name}
            setMultipleImageId={setMultipleImageId}
            deletedCoverContents={deletedCoverContents}
            setDeletedCoverContents={setDeletedCoverContents}
            onLoading={setDisableSubmitButton}
            isAutoUploadOnSelectFile={false}
            maxVideo={1}
            maxImage={5}
            isolatedMultipleCoverContent={!isUpdate && isolatedMultipleCoverContent} // use isolated state instead to avoid mixing value when visiting product detail page
            setIsolatedMultipleCoverContent={!isUpdate && setIsolatedMultipleCoverContent}
          />

          <FormGroup>
            <Label for="startAt">Waktu Mulai Penjualan</Label>
            <div className="mb-1">
              <Controller
                name="startAt"
                control={control}
                defaultValue={data.startAt || null}
                render={({ onChange, name, value }) => (
                  <DatePicker
                    id={name}
                    placeholderText="Pilih Tanggal Mulai"
                    showTimeSelect
                    dateFormat="yyyy/MM/dd HH:mm"
                    timeFormat="HH:mm"
                    selected={value ? new Date(value) : null}
                    minDate={new Date()}
                    className={`rui-datetimepicker form-control w-auto mb-1 ${!!errors.startAt ? 'is-invalid' : ''}`}
                    onChange={onChange}
                  />
                )}
              />
            </div>
            <FormFeedback className="d-block">{errors.startAt?.message}</FormFeedback>
            <small>
              Kami akan membuka link pembayaran pada tanggal dan waktu yang anda pilih. Opsional, kosongkan untuk
              langsung membuka penjualan
            </small>
          </FormGroup>
          <FormGroup>
            <Label for="kadaluarsa4">Tanggal Kadaluarsa</Label>
            <div className="mb-1">
              <DatePicker
                placeholderText="Pilih tanggal atau kosongkan"
                dateFormat="yyyy/MM/dd"
                selected={date ? new Date(date) : null}
                minDate={new Date().setDate(new Date().getDate() + 1)}
                className="rui-datetimepicker form-control w-auto"
                onChange={d => setDate(d)}
              />
            </div>
            <small>Kami akan menutup link pembayaran pada tanggal ini (opsional)</small>
          </FormGroup>
          <FormGroup>
            <Label for="notes">Catatan</Label>
            <Input
              type="textarea"
              name="notes"
              id="notes"
              invalid={errors.notes}
              defaultValue={data.notes}
              innerRef={register()}
            />
            <FormFeedback>{errors.notes?.message}</FormFeedback>
            <small>
              Catatan akan dilihat oleh pendaftar/pembeli setelah melakukan pendaftaran/membayar (opsional).{' '}
            </small>
          </FormGroup>
          <FormGroup>
            <Label for="limit">Maksimum Jumlah Pembayaran (Kuota / QTY)</Label>
            <Input
              type="number"
              name="limit"
              id="limit"
              invalid={errors.limit}
              defaultValue={data.limit}
              innerRef={register()}
            />
            <FormFeedback>{errors?.limit?.message}</FormFeedback>
            <small>
              Kami akan menutup link pembayaran setelah melewati batas jumlah maksimal. Kosongkan untuk tanpa limit
              jumlah (unlimited)
            </small>
          </FormGroup>

          {useRedirect ? (
            <FormGroup>
              <Label for="redirectUrl">Download URL</Label>
              <Input
                type="text"
                name="redirectUrl"
                id="redirectUrl"
                placeholder="https://websitesaya.com/file123.rar"
                invalid={errors.redirectUrl}
                defaultValue={data.redirectUrl}
                innerRef={register({ pattern: validator.url, required: validator.required })}
              />
              <FormFeedback>{errors?.redirectUrl?.message}</FormFeedback>
              <small>Pelanggan akan dibawa kehalaman ini setelah membayar</small>
            </FormGroup>
          ) : (
            <>
              <FormGroup>
                <Label for="fileSource">Sumber Konten*</Label>
                <Input
                  id="fileSource"
                  name="fileSource"
                  type="select"
                  onChange={e => {
                    setSourceFile(e.target.value);
                    setMultipleFileId([]);
                    if (!['link', 'video-drm'].includes(e.target.value)) {
                      setVideoEmbedUrlPreview('');
                      setUploadedVideoEmbedUrl('');
                    }
                  }}
                  invalid={errors.fileSource}
                  innerRef={register({ required: validator.required })}
                  value={sourceFile || ''}
                >
                  <option value="">-- Pilih Sumber Konten --</option>
                  {fileOptions.length && <option value="existing">File Lama</option>}
                  <option value="new">Upload File Baru</option>
                  <option value="video-drm">Upload Video Baru (Terproteksi)</option>
                  <option value="link">Link Embed Video (YouTube)</option>
                </Input>
                <FormFeedback>{errors?.fileSource?.message}</FormFeedback>
              </FormGroup>

              {/* HIDE JIKA UPLOAD BARU */}
              {sourceFile === 'existing' && (
                <FormGroup>
                  <Label for="downloadFileIds">Pilih File Lama*</Label>
                  {loadingFiles ? (
                    <span>
                      <LoadingAnimation twodashline />
                    </span>
                  ) : (
                    <Select
                      id="downloadFileIds"
                      name="downloadFileIds"
                      isMulti
                      options={multipleFileId?.length === 5 ? [] : fileOptions}
                      styles={CustomStyles}
                      disabled={loadingFiles}
                      isLoading={loadingFiles}
                      noOptionsMessage={() => {
                        return multipleFileId?.length === 5
                          ? "You've reached the max options value"
                          : 'No options available';
                      }}
                      defaultValue={
                        !data?.redirectUrl
                          ? data?.content?.map(item => ({ value: item?.file?.id, label: item?.file?.filename }))
                          : null
                      }
                      onChange={e => setMultipleFileId(e.map(i => i.value))}
                    />
                  )}
                  {!multipleFileId?.length && (
                    <FormFeedback style={{ display: 'block' }}>File harus diisi</FormFeedback>
                  )}
                </FormGroup>
              )}

              {/* HIDE JIKA PILIH FILE LAMA */}
              {sourceFile === 'new' && (
                <FormGroup>
                  <Label for="files">File / Konten*</Label>
                  <Dropzone
                    fileId={multipleFileId}
                    setFileId={setMultipleFileId}
                    multiple={true}
                    setNotAllowed={setNotAllowed}
                    maxFiles={5}
                    disallowVideo
                  />
                  {!multipleFileId?.length && (
                    <FormFeedback style={{ display: 'block' }}>File harus diisi</FormFeedback>
                  )}
                  {notAllowed && (
                    <FormFeedback style={{ display: 'block' }}>
                      Nama file tidak boleh menggunakan special character!
                    </FormFeedback>
                  )}
                  <small>
                    Ukuran file maksimal 1GB. Untuk file video, gunakan opsi Upload Video Baru.
                  </small>
                </FormGroup>
              )}

              {/* HIDE JIKA PILIH SELAIN UPLOAD VIDEO BARU */}
              {sourceFile === 'video-drm' && (
                <FormGroup>
                  {isUpdate && (uploadedVideoEmbedUrl || existingVideoEmbedUrl) && (
                    <>
                      <Label for="videoEmbedUrlReadonly">Link Video Saat Ini</Label>
                      <Input
                        type="text"
                        id="videoEmbedUrlReadonly"
                        value={uploadedVideoEmbedUrl || existingVideoEmbedUrl}
                        disabled
                        className="mb-10"
                      />
                      <Alert color="info" className="mb-10">
                        Link ini berasal dari video yang sudah kamu upload sendiri dan otomatis terproteksi DRM
                        oleh Mayar. Karena itu, link hanya bisa diakses lewat platform Mayar (bukan link publik
                        yang bisa dibuka bebas di browser). Kalau ingin mengganti video, upload video baru di
                        bawah ini — video baru juga akan otomatis terproteksi DRM.
                      </Alert>
                    </>
                  )}
                  <VideoDrmUpload
                    ref={videoDrmUploadRef}
                    onUploaded={({ url }) => setUploadedVideoEmbedUrl(url)}
                    onLoading={setDisableSubmitButton}
                  />
                </FormGroup>
              )}

              {/* HIDE JIKA PILIH FILE */}
              {sourceFile === 'link' && (
                <FormGroup>
                  <Label for="videoEmbedUrl">Link Video*</Label>
                  <Input
                    type="url"
                    name="videoEmbedUrl"
                    id="videoEmbedUrl"
                    placeholder="https://youtube.com/watch?v=..."
                    invalid={errors.videoEmbedUrl}
                    defaultValue={videoEmbedUrlPreview}
                    innerRef={register({
                      required: validator.required,
                      pattern: validator.url,
                      validate: value =>
                        (/youtube\.com|youtu\.be/.test(value) && isYouTubeUrl(value)) ||
                        'Gunakan link YouTube (watch, shorts, atau youtu.be). Untuk video Bunny, gunakan opsi Upload Video Baru (Terproteksi).'
                    })}
                    onChange={e => setVideoEmbedUrlPreview(e.target.value)}
                  />
                  <FormFeedback>{errors?.videoEmbedUrl?.message}</FormFeedback>
                </FormGroup>
              )}
            </>
          )}

          <FormGroup>
            <CustomInput
              id="useRedirect"
              label="Tidak Pakai File, Pakai Link Saja"
              name="useRedirect"
              type="switch"
              color="primary"
              defaultChecked={data?.redirectUrl}
              onChange={e => setUseRedirect(e.target.checked)}
              innerRef={register()}
            />
            <small>
              Jika menggunakan upload file, setelah membayar konten akan otomatis bisa diakses oleh pembeli. Jika
              menggunakan redirect URL, pelanggan akan dibawa ke halaman redirect URL setelah membayar
            </small>
          </FormGroup>

          <FormGroup>
            <CustomInput
              id="affiliateSwitch"
              label="Produk bisa diaffiliate"
              defaultChecked={enableAffiliate}
              type="switch"
              color="primary"
              onChange={e => setEnableAffiliate(e.target.checked)}
            />
          </FormGroup>

          {enableAffiliate && (
            <FormGroup>
              <Label for="affiliateCommission">Komisi Affiliate</Label>
              <InputGroup>
                <Input
                  type="number"
                  name="affiliateCommission"
                  id="affiliateCommission"
                  defaultValue={data?.affiliateCommission}
                  invalid={errors.affiliateCommission}
                  innerRef={register({
                    min: validator.min(1),
                    max: validator.max(70)
                  })}
                />
                <InputGroupAddon addonType="prepend">
                  <InputGroupText>%</InputGroupText>
                </InputGroupAddon>

                <FormFeedback>{errors.affiliateCommission?.message}</FormFeedback>
                <small>
                  Komisi secara otomatis dipotong dari persentase total harga penjualan (setelah dipotong kode diskon
                  jika ada, biaya channel dan biaya Mayar). Komisi hanya untuk affiliator yang terdaftar pada produk
                  ini.
                </small>
              </InputGroup>
            </FormGroup>
          )}

          <SubmitButton
            size="lg"
            block
            color="brand"
            type="submit"
            className="text-center"
            text={`${isUpdate ? 'Update' : 'Buat'} Produk Digital`}
            loading
            isDisabled={disableSubmitButton}
            isLoading={
              insertLoading ||
              updateLoading ||
              insertUserContentLoading ||
              removeUserContentLoading ||
              upsertVideoEmbedLoading ||
              insertVideoEmbedFileLoading ||
              editVideoEmbedFileLoading
            }
          />
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default ModalBodyDigitalProduct;
